import { useEffect } from 'react'

export interface QueueKeyHandlers {
  next: () => void
  previous: () => void
  /** Triage, not authorisation: close means a person has dealt with it. */
  close: () => void
  dismiss: () => void
  note: () => void
  reopen: () => void
  openTrace: () => void
  escape: () => void
}

/**
 * The queue's keyboard model, in one place because it is easy to get subtly
 * wrong when the drawer, the note field and the rows are separate components.
 *
 * Two rules the spec is explicit about and both are about not surprising
 * someone: the bindings do nothing while focus is in a field (so typing "a" in
 * a note does not approve anything), and nothing but Escape works while an
 * overlay is open.
 */
export function useQueueKeys(handlers: QueueKeyHandlers, options: { enabled: boolean; overlayOpen: boolean }) {
  useEffect(() => {
    if (!options.enabled) return

    function onKeyDown(event: KeyboardEvent) {
      const target = event.target as HTMLElement | null
      const typing =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target?.isContentEditable === true

      if (event.key === 'Escape') {
        handlers.escape()
        return
      }
      if (typing || options.overlayOpen) return
      if (event.metaKey || event.ctrlKey || event.altKey) return

      switch (event.key) {
        case 'j': case 'ArrowDown': event.preventDefault(); handlers.next(); break
        case 'k': case 'ArrowUp': event.preventDefault(); handlers.previous(); break
        case 'c': handlers.close(); break
        case 'x': handlers.dismiss(); break
        case 'n': event.preventDefault(); handlers.note(); break
        case 'u': handlers.reopen(); break
        case 'Enter': event.preventDefault(); handlers.openTrace(); break
        default: break
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [handlers, options.enabled, options.overlayOpen])
}
